import React from 'react';
import cn from 'classnames';
import FinalForm, { Field as BaseField } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { Field as FieldPrimitive, Input } from '@economic/taco';
import { nanoid } from 'nanoid';
import { t } from '@lingui/macro';
import CheckboxField from './fields/FieldCheckbox';
import ComboboxField from './fields/FieldCombobox';
import DatePickerField from './fields/FieldDatePicker';
import DropdownField from './fields/FieldDropdown';
import TextField from './fields/FieldText';
import { RadioGroupField, NumberRadioGroupField } from './fields/FieldRadioGroup';
import NumberField from './fields/FieldNumber';
import FragmentField from './fields/FieldFragment';
import { useFormFieldRegistration } from './Form';
import FieldTextArea from './fields/TextArea';
import FieldSwitch from './fields/FieldSwitch';
import { formFieldSizeConstants } from '../../../pages/accounting/journals/utils/FormFieldConstants';
import { TruncatedElement } from './TruncatedElement';
import { FieldSizeId } from '../ui/utils/types';

export interface FieldProps<T> extends FinalForm.FieldProps<any, any> {
    as?: React.ComponentType<T> | 'input';
    fieldClassName?: string;
    hideLabel?: boolean;
    label?: React.ReactNode;
    required?: boolean;
    tabIndex?: number;
    truncateLabel?: boolean;
    __legacyFieldSize?: FieldSizeId; // Used for form1 legacy compatibility, allowing user defined field width
}

const Field = ({
    as,
    'aria-label': ariaLabel,
    component: _,
    disabled,
    fieldClassName,
    hideLabel = false,
    id,
    label,
    required,
    supplementary,
    wrapWithLabel = true,
    truncateLabel = false,
    truncateMessage = false, // truncate field message to label size
    tabIndex,
    __legacyFieldSize,
    ...props
}: FieldProps<any>) => {
    const Component = as || Input;
    const registerField = useFormFieldRegistration();
    const fieldSize = formFieldSizeConstants.find(ffsc => ffsc.id === __legacyFieldSize)?.width;
    const requiredSign = required ? ' *' : '';
    let fieldId = id;

    // When wrapWithLabel is false, then we rely on htmlFor attribute to specify which component the label is bound to.
    // Hence it is necessary to create a unique id if it wasn't passed
    if (!wrapWithLabel && !fieldId) {
        fieldId = nanoid();
    }

    React.useEffect(() => {
        registerField(props.name, label);
    }, []);

    let validate;

    if (required) {
        validate = async (value, allValues) => {
            if (props.validate) {
                const validation = await props.validate(value, allValues);

                if (validation) {
                    return validation;
                }
            }

            return value === undefined || value === null ? t`Required` : undefined;
        };
    } else {
        validate = props.validate;
    }

    return (
        <div className={cn('field', fieldClassName)}>
            <BaseField
                render={({ input, meta, summary, ...componentProps }: any) => {
                    const error = meta.error || meta.submitError;
                    const labelProps = {
                        disabled: componentProps.disabled,
                        invalid: meta.touched && error ? true : !meta.touched ? componentProps.invalid : false,
                        message: meta.touched && error ? error : !meta.touched ? componentProps.message : undefined,
                    };
                    const fieldComponent = (
                        <Component
                            {...input}
                            {...componentProps}
                            aria-label={ariaLabel}
                            invalid={labelProps.invalid || (!!meta.touched && !!error)}
                            tabIndex={tabIndex}
                        />
                    );
                    let output;

                    if (hideLabel) {
                        output = fieldComponent;
                    } else if (wrapWithLabel) {
                        output = (
                            <FieldPrimitive
                                {...labelProps}
                                style={{ width: fieldSize || (truncateMessage ? props.style?.width : undefined) }}>
                                {label && (
                                    <TruncatedElement truncate={truncateLabel}>
                                        <>
                                            {label}
                                            {requiredSign}
                                        </>
                                    </TruncatedElement>
                                )}
                                {fieldComponent}
                            </FieldPrimitive>
                        );
                    } else {
                        output = (
                            <div className={cn('pb-4', { 'min-h-[4.5rem]': !fieldClassName?.includes('field--no-height') })}>
                                <FieldPrimitive className="pb-0 !min-h-0" htmlFor={fieldId} {...labelProps}>
                                    {label && (
                                        <TruncatedElement truncate={truncateLabel}>
                                            <>
                                                {label}
                                                {requiredSign}
                                            </>
                                        </TruncatedElement>
                                    )}
                                </FieldPrimitive>
                                {fieldComponent}
                            </div>
                        );
                    }

                    return supplementary ? (
                        <div className="relative">
                            {output}
                            {supplementary && (
                                <div className="field__label__container__supplementary__field absolute top-0 right-0 -mt-1">
                                    {supplementary}
                                </div>
                            )}
                        </div>
                    ) : (
                        output
                    );
                }}
                parse={value => (value === null ? undefined : value)}
                {...props}
                id={fieldId}
                disabled={disabled}
                validate={validate}
            />
        </div>
    );
};

Field.Checkbox = CheckboxField;
Field.Combobox = ComboboxField;
Field.DatePicker = DatePickerField;
Field.Dropdown = DropdownField;
Field.Text = TextField;
Field.RadioGroup = RadioGroupField;
Field.NumberRadioGroup = NumberRadioGroupField;
Field.Number = NumberField;
Field.Fragment = FragmentField;
Field.Base = BaseField;
Field.Hidden = props => <BaseField {...props} component="input" type="hidden" />;
Field.Array = FieldArray;
Field.TextArea = FieldTextArea;
Field.Switch = FieldSwitch;

export default Field;
